import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, AbstractControlOptions, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';

import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { Digest } from '@Monitor/Briefings/models/digest.model';
import { validateEmail } from '@Common/Misc/helpers/formatting/email.helper';
import { MailingListsService } from '@Common/MailingLists/services/mailing-lists.service';
import { DigestService } from '@Monitor/Shared/services/digest.service';
import { DigestShareRequestApi } from '@api/briefings/model/digest-share-request-api';
import { CurrentUserStateService } from '@Core/Users/services/current-user-state.service';
import { Subscription } from 'rxjs';

@Component({
    selector: 'agpr-digest-share-dialog',
    templateUrl: './digest-share-dialog.component.html',
    styleUrls: ['./digest-share-dialog.component.scss'],
    standalone: false
})
export class DigestShareDialogComponent extends AbstractDialogComponent implements OnInit, OnDestroy {
    readonly buttonType = ButtonTypesEnum;

    @Input() digest: Digest;

    form!: FormGroup;

    showMailingListError = false;
    showRecipientError = false;
    subscriptions: Subscription[] = [];

    constructor(
        public coreDialogService: CoreDialogService,
        private fb: FormBuilder,
        private mailingListsService: MailingListsService,
        private digestService: DigestService,
        private currentUserStateService: CurrentUserStateService,
    ) {
        super(coreDialogService, 'DigestShare');
    }

    ngOnInit(): void {
        this.form = this.fb.group({
            mailingListIds: [[] as number[]],
            recipientList: [[] as string[]],
            fromName: ['', Validators.required],
            replyTo: ['', [Validators.required, this.emailValidator]],
            inviteMessage: [''],
            excludeExistingRecipients: [false]
        } as AbstractControlOptions);

        this.mailingListIdsControl?.valueChanges.subscribe(() => {
            this.showMailingListError = false;
            this.showRecipientError = false;
        });

        this.recipientListControl?.valueChanges.subscribe(() => {
            this.showMailingListError = false;
            this.showRecipientError = false;
        });

        this.subscriptions.push(
            this.currentUserStateService.getUser().subscribe(user => {
                const currentUserEmail = user?.email || '';
                let fromName = '';

                if (user?.firstName && user?.lastName) {
                    fromName = `${user.firstName} ${user.lastName}`;
                }
                this.form.patchValue({
                    fromName: fromName,
                    replyTo: currentUserEmail
                });
            })
        );
    }

    get mailingListIdsControl(): AbstractControl | null {
        return this.form.get('mailingListIds');
    }

    get recipientListControl(): AbstractControl | null {
        return this.form.get('recipientList');
    }

    get fromNameControl(): AbstractControl | null {
        return this.form.get('fromName');
    }

    get replyToControl(): AbstractControl | null {
        return this.form.get('replyTo');
    }

    get inviteMessageControl(): AbstractControl | null {
        return this.form.get('inviteMessage');
    }

    get excludeExistingRecipientsControl(): AbstractControl | null {
        return this.form.get('excludeExistingRecipients');
    }

    emailValidator(control: AbstractControl): ValidationErrors | null {
        const value = control.value;

        if (!value || value.trim() === '') {
            return null;
        }

        return validateEmail(value) ? null : { invalidEmail: true };
    }

    onRecipientListChange(value: string[]): void {
        this.recipientListControl?.setValue(Array.isArray(value) ? value : []);
        this.recipientListControl?.markAsDirty();
        this.recipientListControl?.markAsTouched();
        this.recipientListControl?.updateValueAndValidity();

        this.showMailingListError = false;
        this.showRecipientError = false;
    }

    share() {
        if (!this.validateForm()) {
            return;
        }

        const formValue = this.form.getRawValue();

        const shareRequest: DigestShareRequestApi = {
            emails: formValue.recipientList,
            mailingListIds: formValue.mailingListIds,
            fromName: formValue.fromName,
            replyTo: formValue.replyTo,
            inviteMessage: formValue.inviteMessage,
            excludeSubscribers: formValue.excludeExistingRecipients
        };

        this.digestService.share(this.digest, shareRequest).subscribe(() => {
            this.closeDialog();
        });
    }

    validateForm(): boolean {
        this.resetErrors();
        this.form.markAllAsTouched();

        const mailingListIds = Array.isArray(this.mailingListIdsControl?.value)
            ? this.mailingListIdsControl?.value
            : [];

        const recipientList = Array.isArray(this.recipientListControl?.value)
            ? this.recipientListControl?.value
            : [];

        if (mailingListIds.length > 0 && this.mailingListsService.getRecipientCount(mailingListIds) === 0) {
            this.showMailingListError = true;
        }

        if (mailingListIds.length === 0 && recipientList.length === 0) {
            this.showRecipientError = true;
        }

        return this.form.valid
            && !this.showMailingListError
            && !this.showRecipientError;
    }

    resetErrors(): void {
        this.showMailingListError = false;
        this.showRecipientError = false;
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
